import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';

import { PaymentDirectionEnum, PaymentModel } from 'projects/workspace/src/app/payment/models/payment.model';
import { IncomingInvoiceApiService } from 'projects/workspace/src/app/incoming-invoice/services/invoice-api.service';
import { PurchaseOrderApiService } from 'projects/workspace/src/app/purchase-order/services/purchase-order-api.service';
import { SubscriptionApiService } from 'projects/workspace/src/app/subscription/services/subscription-api.service';
import { DeliveryNoteApiService } from 'projects/workspace/src/app/delivery-note/services/delivery-note-api.service';
import { IncomingInvoiceModel } from 'projects/workspace/src/app/incoming-invoice/models/incoming-invoice.model';
import { OutgoingInvoiceModel } from 'projects/workspace/src/app/outgoing-invoice/models';
import { TradeOfferApiService } from 'projects/workspace/src/app/trade-offer/services/trade-offer-api.service';
import { SalesOrderApiService } from 'projects/workspace/src/app/sales-order/services/sales-order-api.service';
import { SubscriptionModel } from 'projects/workspace/src/app/subscription/models/subscription.model';
import { InvoiceApiService } from 'projects/workspace/src/app/outgoing-invoice/services/invoice-api.service';
import { PaymentApiService } from 'projects/workspace/src/app/payment/services/payment-api.service';
import { TradeOfferModel } from 'projects/workspace/src/app/trade-offer/models';
import { SalesOrderModel } from 'projects/workspace/src/app/sales-order/models/sales-order.model';
import { PurchaseOrder } from 'projects/workspace/src/app/purchase-order/models';
import { DeliveryNote } from 'projects/workspace/src/app/delivery-note/models/delivery-note.model';
import { CreateNewEmployeeModalComponent } from '../modules/modals/modals-hrm';
import { CreateNewPartnerModalComponent, CreateNewPrivatePartnerModalComponent } from '../modules/modals/modals-crm';
import { CompositeProductModalComponent } from '../modules/modals/modals-products/composite-product-modal/composite-product-modal.component';
import { ProductTypes } from '../modules/products/product-types';
import { TimeTrackingLogTimeModalComponent } from '../modules/modals/modals-time-tracking';
import { selectCompanyProfile } from 'projects/workspace/src/app/administration/store/selectors';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { CreateNewTransactionModalComponent } from '../modules/modals/modals-transactions';
import { CreateNewBankAccountModalComponent } from '../modules/modals/modals-bank-accounts';
import { UserPermissionsService } from './user-permissions.service';
import { ToasterService } from '../modules/ui-components/toaster';
import { BankAccountsApiService } from 'projects/workspace/src/app/bank-accounts/services/bank-accounts-api.service';
import { BankAccountModel } from 'projects/workspace/src/app/bank-accounts/models';
import { ScheduledPaymentApiService } from 'projects/workspace/src/app/scheduled-payment/services/scheduled-payment-api.service';
import { ScheduledPaymentModel } from 'projects/workspace/src/app/scheduled-payment/models';
import { InfoModalComponent, UpgradePlanModalComponent } from '../modules/modals/modals-common';
import {
  CreditNoteCreateModalComponent
} from '../modules/modals/modals-credit-note/credit-note-create-modal/credit-note-create-modal.component';
import { SbcPermissionEnum } from 'projects/workspace/src/app/administration/enums/sbc-permission.enum';
import { AccountingAccessService } from './accounting-access.service';


@Injectable()
export class ActionButtonsService {

  public paymentType: typeof PaymentDirectionEnum = PaymentDirectionEnum;
  public companyProfile: CompanyProfile;
  public barcodeDialogEmitter: Subject<any> = new Subject<any>();
  public destroy$: Subject<any> = new Subject<any>();

  public displayHints: Subject<any> = new Subject<any>();
  public displaySidebar: Subject<number> = new Subject<number>();
  public hidePositionSidebar: Subject<any> = new Subject<any>();

  constructor( public router: Router,
               private invoiceApiService: InvoiceApiService,
               private store: Store<AppState>,
               private incomingInvoiceApiService: IncomingInvoiceApiService,
               private scheduledPaymentApiService: ScheduledPaymentApiService,
               private tradeOfferApi: TradeOfferApiService,
               private deliveryNoteApiService: DeliveryNoteApiService,
               private paymentApiService: PaymentApiService,
               // private exchangeApiService: ExchangeApiService,
               private readonly salesOrderApi: SalesOrderApiService,
               private readonly poApi: PurchaseOrderApiService,
               private readonly subscriptionApi: SubscriptionApiService,
               private readonly bankAccountsApiService: BankAccountsApiService,
               private dialog: MatDialog,
               private readonly userPermissionsService: UserPermissionsService,
               private readonly toasterService: ToasterService,
               private readonly accountingAccessService: AccountingAccessService,
  ) {
    this.store.select(selectCompanyProfile)
      .subscribe((companyProfile: CompanyProfile) => {
        this.companyProfile = companyProfile;
      });
  }

  public callAction(actionName: string): void {
    if (this.companyProfile.status === 'PAUSED' && this.companyProfile.entityState === 'EXISTS') {
      // subscription activation is over
      return;
    }
    if (actionName && this[actionName]) {
      this[actionName]();
    } else {
      console.warn(`Can not call quick action with ${actionName}, it doesn't exist`);
    }
  }

  public createNewInvoice() {
    if (this.accountingAccessService.denyWriteAccess()) { return; }

    this.invoiceApiService.createOutgoingInvoiceBlank()
      .subscribe((data: OutgoingInvoiceModel) => {
          this.router.navigate([`/accounting/outgoing-invoice/${data.invoiceId}`]);
        });
  }

  public createNewIncomingInvoice() {
    if (this.accountingAccessService.denyWriteAccess()) { return; }

    // this.dialog.open(CreateIinModalComponent, {
    //   minWidth: '624px',
    //   backdropClass: 'backdrop-none',
    //   disableClose: true
    // });
    this.incomingInvoiceApiService.createIncomingInvoiceBlank()
      .subscribe((data: IncomingInvoiceModel) => {
          this.router.navigate([`/accounting/incoming-invoice/${data.id}`]);
        });
  }

  public createNewScheduledPayment() {
    this.scheduledPaymentApiService.createScheduledPaymentBlank()
      .subscribe((data: ScheduledPaymentModel) => {
        this.router.navigate([`/accounting/scheduled-payment/${data.id}`]);
      });
  }

  public createNewOffer() {
    this.tradeOfferApi.createTradeOfferDraft()
      .subscribe((data: TradeOfferModel) => {
          this.router.navigate([`/trade/offers/${data.id}`]);
        });
  }

  public createNewOrder() {
    if (this.isActionDisabledByPlan(SbcPermissionEnum.SALES_ORDERS, this.createNewOrder)) { return; }
    this.salesOrderApi.createSalesOrderDraft()
      .subscribe((data: SalesOrderModel) => {
        this.router.navigate([`/trade/sales-order/${data.id}`]);
      });
  }

  public createNewPurchaseOrder() {
    this.poApi.createDefaultPurchaseOrder()
      .subscribe((data: PurchaseOrder) => {
          this.router.navigate([`/trade/purchase-order/${data.id}`]);
        });
  }

  public createNewERA() {
    this.dialog.open(InfoModalComponent, {
      data: {
        title: 'DASHBOARDS.UPDATING_MODAL_TITLE',
        message: 'DASHBOARDS.UPDATING_MODAL_MSG',
        hideConfirmBtn: true,
        cancelBtnClass: 'btn-primary',
        cancelBtnText: 'BUTTON.CLOSE',
      },
      disableClose: true,
    });
    // this.exchangeApiService.createExchangeDraft()
    //   .subscribe((data: ExchangeModel) => {
    //     this.router.navigate([`/trade/exchanges-and-returns/${data.id}`]);
    //   });
  }

  public createNewDeliveryNote() {
    if (this.isActionDisabledByPlan(SbcPermissionEnum.OUTGOING_DELIVERIES, this.createNewDeliveryNote)) { return; }
    this.deliveryNoteApiService.createDeliveryNoteDraft()
      .subscribe((deliveryNote: DeliveryNote) => {
          this.router.navigate([`/warehouse/outgoing-delivery/${deliveryNote.id}`]);
        });
  }

  public createNewIncomingPayment() {
    if (this.accountingAccessService.denyWriteAccess()) { return; }

    this.paymentApiService.createPaymentBlank(this.paymentType.INCOMING)
      .subscribe((payment: PaymentModel) => {
          this.router.navigate([`/accounting/payments/${payment.paymentId}`]);
        });
  }

  public createNewOutgoingPayment() {
    if (this.accountingAccessService.denyWriteAccess()) { return; }

    this.paymentApiService.createPaymentBlank(this.paymentType.OUTGOING)
      .subscribe((payment: PaymentModel) => {
          this.router.navigate([`/accounting/payments/${payment.paymentId}`]);
        });
  }

  public createNewSubscription() {
    if (this.isActionDisabledByPlan(SbcPermissionEnum.RECURRING_INVOICES, this.createNewSubscription)) { return; }

    this.subscriptionApi.createSubscriptionDraft()
      .subscribe((data: SubscriptionModel) => {
        this.router.navigate([`/accounting/recurring-invoice/${data.id}`]);
      });
  }

  public createNewEmployee() {
    if (this.isActionDisabledByFreePlan(SbcPermissionEnum.TEAM_USERS, this.createNewEmployee)) { return; }

    this.dialog.open(CreateNewEmployeeModalComponent, {
      data: {},
      disableClose: true,
    });
  }

  public createNewProduct() {
    this.dialog.open(CompositeProductModalComponent, {
      data: {
        family: null,
        productType: ProductTypes.GOODS
      },
      disableClose: true,
    });
  }

  public createNewService() {
    this.dialog.open(CompositeProductModalComponent, {
      data: {
        family: null,
        productType: ProductTypes.SERVICES
      },
      disableClose: true,
    });
  }

  public createNewDigitalProduct() {
    this.dialog.open(CompositeProductModalComponent, {
      data: {
        family: null,
        productType: ProductTypes.DIGITAL
      },
      disableClose: true,
    });
  }

  public createNewBundleProduct() {
    this.dialog.open(CompositeProductModalComponent, {
      data: {
        family: null,
        productType: ProductTypes.BUNDLE
      },
      disableClose: true,
    });
  }

  public createNewPartner() {
    this.dialog.open(CreateNewPartnerModalComponent, {
      disableClose: true,
    });
  }

  public createNewPrivatePartner() {
    this.dialog.open(CreateNewPrivatePartnerModalComponent, {
      disableClose: true,
    });
  }

  public createTransaction() {
    this.dialog.open(CreateNewTransactionModalComponent, {
      disableClose: true,
      minWidth: '640px',
      maxWidth: '640px',
    });
  }

  public createNewCreditNote() {
    if (this.accountingAccessService.denyWriteAccess()) { return; }

    this.dialog.open(CreditNoteCreateModalComponent, {
      disableClose: true,
    });
  }

  public onLogTimeClick(): void {
    this.dialog.open(TimeTrackingLogTimeModalComponent, {
      data: null,
      disableClose: true,
    });
  }

  public barcodeDialogToggle() {
    this.barcodeDialogEmitter.next();
  }

  public createBankAccountManually(): void {
    if (!this.userPermissionsService.canManageAccounting()) {
      this.toasterService.notify({type: 'error', message: 'COMMON.NO_ACCESS_ACCOUNTING'});
      return;
    }

    this.bankAccountsApiService.getBankAccountPrimary()
      .pipe(takeUntil(this.destroy$))
      .subscribe((bankAccount: BankAccountModel) => {
        this.dialog.open(CreateNewBankAccountModalComponent, {
          data: {
            primaryBankAccountExists: bankAccount && bankAccount.primaryAccount
          },
          disableClose: true,
        });
      });
  }

  public createBankAccount(): void {
    if (!this.userPermissionsService.canManageAccounting()) {
      this.toasterService.notify({type: 'error', message: 'COMMON.NO_ACCESS_ACCOUNTING'});
      return;
    }

    if (this.isActionDisabledByPlan(SbcPermissionEnum.OPEN_BANKING, this.createBankAccount)) { return; }

    this.router.navigate(['/accounting/bank-accounts/create-banks-list']);
  }

  public isActionDisabledByPlan(feature: SbcPermissionEnum, cbFunction?: Function): boolean {
    if (!this.companyProfile.subscriptionManagement.subscriptionPermissions[feature]) {
      this.dialog.open(UpgradePlanModalComponent, { data: {
          validationFeature: feature,
          cbFunction: cbFunction
            ? cbFunction.bind(this)
            : null
        }});
    }

    return !this.companyProfile.subscriptionManagement.subscriptionPermissions[feature];
  }

  public isActionDisabledByFreePlan(feature: SbcPermissionEnum, cbFunction?: Function): boolean {
    const isActionDisabledByPlan = this.companyProfile.isFreePlan;

    if (isActionDisabledByPlan) {
      this.dialog.open(UpgradePlanModalComponent, { data: {
          validationFeature: feature,
          cbFunction: cbFunction.bind(this)
        }});
    }

    return isActionDisabledByPlan;
  }

  public startHints(): void {
    this.displayHints.next();
  }

  public displaySidebarHandler(productId: number): void {
    this.displaySidebar.next(productId);
  }

  public hideSidebarHandler(): void {
    this.hidePositionSidebar.next();
  }

}
